import {
  DeploymentRingFilter,
  DeploymentRingFilterValues
} from "modules/field-mgmt/sections/device/field-device-deployment-rings/DeploymentRingFilter";
import { DeploymentRingTable } from "modules/field-mgmt/sections/device/field-device-deployment-rings/DeploymentRingTable";
import React, { useState } from "react";

import {
  CheckboxField,
  FieldItem,
  Heading,
  ISelection,
  Selection,
  SelectionMode,
  Stack,
  SubmissionForm,
  useTheme
} from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import {
  BulkActionResponse,
  DeploymentRing
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useAssignDeploymentRingBulkAction,
  useDeploymentRings
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import {
  BulkActionType,
  BulkAssignDeploymentRingFormValues
} from "../../formTypes";
import { BulkActionFormWrapper } from "../BulkActionFormWrapper";

const validator = new Validator<BulkAssignDeploymentRingFormValues>();

const schema: ValidationSchema<BulkAssignDeploymentRingFormValues> = {
  selectorId: validator.string().required({ message: "Selector is required" }),
  actionType: validator
    .string()
    .required({ message: "Action Type is required" }),
  deploymentRingId: validator
    .string()
    .required({ message: "Deployment ring is required" })
};

export const AssignDeploymentRingForm: React.FC = () => {
  const theme = useTheme();
  const query = useDeploymentRings();
  const [responseMessage, setResponseMessage] = useState<
    BulkActionResponse | undefined
  >();

  const {
    mutateAsync: assignDeploymentRingAction
  } = useAssignDeploymentRingBulkAction();

  const onSubmit = async (formValues: BulkAssignDeploymentRingFormValues) => {
    const { selectorId, deploymentRingId, assignHighestPriority } = formValues;
    if (assignHighestPriority === undefined || !deploymentRingId || !selectorId)
      return;

    // Submit request for bulk action
    const response = await assignDeploymentRingAction({
      assignHighestPriority,
      deploymentRingId,
      selectorId
    });

    setResponseMessage(response);
  };

  return (
    <SubmissionForm<BulkAssignDeploymentRingFormValues>
      onSubmit={onSubmit}
      initialValues={{
        actionType: BulkActionType.AssignDeploymentRing,
        deploymentRingId: undefined,
        selectorId: undefined,
        assignHighestPriority: false
      }}
      validate={values => validator.validate(values, schema)}
      hideButtons
    >
      {(state, actions) => {
        // Required to register deploymentRingId field since it does not exist af field control
        !state.fields["deploymentRingId"] &&
          actions.registerField("deploymentRingId");

        return (
          <BulkActionFormWrapper responseMessage={responseMessage}>
            <CheckboxField
              label="Assign as highest priority"
              name="assignHighestPriority"
            />
            <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
              <Heading variant="modal-heading">Select Deployment Ring</Heading>

              <QueryStateIndicator<DeploymentRing[]>
                allowNullOrUndefined
                {...query}
              >
                {deploymentRings => (
                  <Stack verticalFill>
                    <Stack
                      verticalFill
                      verticalAlign="space-between"
                      tokens={{ childrenGap: theme.spacing.s1 }}
                    >
                      <DeploymentRingFilter>
                        {({ values }) => {
                          const selection = new Selection<DeploymentRing>({
                            getKey: item => item.id, // This is required to keep the selected item selected when the filter changes (otherwise it uses the index as the key)
                            onSelectionChanged: async () => {
                              const deploymentRings = selection.getSelection();
                              const deploymentRingValue =
                                deploymentRings.length > 0
                                  ? deploymentRings[0].id
                                  : "";

                              actions.setValue(
                                "deploymentRingId",
                                deploymentRingValue
                              );
                            }
                          });

                          return (
                            <FieldItem
                              name="deploymentRingId"
                              errorMessage={
                                state.triedToSubmit &&
                                state.fields["deploymentRingId"]?.error
                                  ? state.fields["deploymentRingId"].error
                                  : undefined
                              }
                            >
                              <DeploymentRingTable
                                items={filterAndSortDeploymentRings(
                                  deploymentRings,
                                  values
                                )}
                                stickyHeader
                                selectionMode={SelectionMode.single}
                                selection={selection as ISelection}
                                setKey="deploymentRing-Table"
                              />
                            </FieldItem>
                          );
                        }}
                      </DeploymentRingFilter>
                    </Stack>
                  </Stack>
                )}
              </QueryStateIndicator>
            </Stack>
          </BulkActionFormWrapper>
        );
      }}
    </SubmissionForm>
  );
};

const filterAndSortDeploymentRings = (
  deploymentRings: DeploymentRing[],
  filter: DeploymentRingFilterValues
): DeploymentRing[] => {
  return deploymentRings
    .filter(
      d =>
        filter.name === "" ||
        d.displayName.toLowerCase().indexOf(filter.name.toLowerCase()) >= 0
    )
    .sort((a: DeploymentRing, b: DeploymentRing) => {
      if (a.isPublic !== b.isPublic) {
        return a.isPublic ? 1 : -1;
      }

      return 0;
    });
};
